import cachedHttpClient from '../cached-http-client';
import { httpClient } from '../http-client';
import { ApiResponse } from '../../types/responses/api-response';

/**
 * Клиент экрана «Аналитика пользователей» (`GET /analytics/users*`).
 *
 * AD-93. Файл описывал ответ, которого сервер не отдавал: у строки списка было
 * двадцать выдуманных полей (`totalSpent`, `tasksCompleted`, `socialConnections`,
 * `loginFrequency`, …), метрики объявлялись шестью разделами (`overview`,
 * `engagement`, `performance`, `social`, `financial`, `trends`), а три метода
 * обращались к маршрутам, которых в контроллере нет вовсе
 * (`/analytics/users/:id`, `/analytics/users/trends`, `/analytics/users/export`) —
 * все три отвечали 404, и экран прятал это в `console.error`. Здесь остались
 * ровно те три маршрута, которые объявляет `users-analytics.controller.ts`, и
 * ровно те поля, которые они возвращают.
 */
export interface UserAnalytics {
  id: string;
  name: string;
  email: string;
  /** Сервер отдаёт `null`, когда аватара нет. */
  avatar?: string | null;
  /** Контекстная роль (`users.role`): admin, manager, user, organizer, … */
  role: string;
  /** Как записано в `users.department`; пустая строка — отдел не указан. */
  department: string;
  status: 'active' | 'inactive' | 'blocked';
  registrationDate: string;
  lastLoginDate: string | null;
  /** Участий за период (период на этом экране — окно активности). */
  totalEvents: number;
  eventsAttended: number;
  /** Средняя оценка посещённых мероприятий, 0–5. */
  averageRating: number;
  /** AD-93: шкала 0–100 — та же, в которой рисуется полоса прогресса. */
  engagementScore: number;
  // AD-92: ступеней три. Сервер выводит уровень из числа участий
  // (users-analytics.vocabulary.ts) и `very_high` не возвращает никогда.
  activityLevel: 'low' | 'medium' | 'high';
}

export interface UserMetrics {
  totalUsers: number;
  activeUsers: number;
  /** pending + suspended на стороне базы. */
  inactiveUsers: number;
  blockedUsers: number;
  /** Зарегистрированы внутри выбранного периода. */
  newUsers: number;
  /** Участий на человека за период. */
  averageEventsPerUser: number;
  /** Средняя оценка посещённых мероприятий, 0–5. */
  averageRating: number;
  /** Доля посещённых регистраций, 0–100. */
  averageAttendanceRate: number;
  /** Вовлечённость когорты по той же формуле, что и в строке списка, 0–100. */
  averageEngagement: number;
  topDepartments: Array<{ department: string; count: number; percentage: number }>;
  topRoles: Array<{ role: string; count: number; percentage: number }>;
}

/**
 * Разрезы `GET /analytics/users/segmentation`. AD-93: ступень активности
 * называется `level`, а не `activityLevel`; разрезов `byPerformance` и
 * `byEngagement` сервер не отдаёт вовсе, зато отдаёт `byStatus`.
 */
export interface UsersSegmentation {
  byRole: Array<{ role: string; count: number; percentage: number }>;
  byDepartment: Array<{
    department: string;
    count: number;
    percentage: number;
  }>;
  byActivity: Array<{ level: string; count: number; percentage: number }>;
  byStatus: Array<{ status: string; count: number; percentage: number }>;
}

/**
 * Карточка одного пользователя (`GET /analytics/users/:id`) — экран
 * `/users/:id`. AD-93: до этой правки экран объявлял у метрик
 * `satisfactionScore` и `responseTime`, которых нет ни в одной таблице, а сам
 * маршрут на сервере отсутствовал — карточки «Активность пользователя» и
 * «Мероприятия» показывали ошибку при каждом открытии страницы.
 */
export interface UserAnalyticsCard {
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
    /** Как записано в `users.department`; пустая строка — отдел не указан. */
    department: string;
    status: 'active' | 'inactive' | 'blocked';
    registrationDate: string;
    /** `null` — не входил ни разу. */
    lastLoginDate: string | null;
    activityLevel: 'low' | 'medium' | 'high';
  };
  metrics: {
    /** Счётчики карточки — за ВСЁ время, окно активности к ним не применяется. */
    totalEvents: number;
    eventsAttended: number;
    /** Доля посещённых регистраций, 0–100. */
    attendanceRate: number;
    /** Средняя оценка посещённых мероприятий, 0–5; ноль — не оценивал никого. */
    averageRating: number;
    /** Шкала 0–100, та же формула, что в списке и метриках когорты. */
    engagementScore: number;
    activityLevel: 'low' | 'medium' | 'high';
    tasksAssigned: number;
    tasksCompleted: number;
    /** `null` — задач не назначено, доли не существует. */
    taskCompletionRate: number | null;
  };
  events: Array<{
    /** Идентификатор УЧАСТИЯ, а не мероприятия. */
    id: string;
    eventId: string;
    title: string;
    type: string;
    date: string;
    /** Роль на мероприятии: attendee, speaker, moderator, … */
    role: string;
    status: string;
    /** `null` — участник не оценивал. */
    rating: number | null;
  }>;
}

export interface UsersFilters {
  dateRange?: string;
  role?: string;
  department?: string;
  status?: string;
  activityLevel?: string;
  page?: number;
  limit?: number;
}

export const usersAnalyticsApi = {
  // Получение списка пользователей с аналитикой
  getUsers: async (filters: UsersFilters = {}): Promise<ApiResponse<{
    users: UserAnalytics[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }>> => {
    return cachedHttpClient.get('/analytics/users', { params: filters });
  },

  // Получение метрик пользователей
  getUsersMetrics: async (filters: UsersFilters = {}): Promise<ApiResponse<UserMetrics>> => {
    return cachedHttpClient.get('/analytics/users/metrics', { params: filters });
  },

  /**
   * Карточка одного пользователя. Клиент — НЕкэширующий намеренно: на экране
   * есть кнопка «Обновить аналитику», а `cachedHttpClient` держит ответ пять
   * минут и превратил бы её в кнопку, которая ничего не делает.
   */
  getUserAnalytics: async (
    userId: string,
  ): Promise<ApiResponse<UserAnalyticsCard>> => {
    return httpClient.get(`/analytics/users/${userId}`);
  },

  // Получение сегментации пользователей
  getUsersSegmentation: async (
    filters: UsersFilters = {},
  ): Promise<ApiResponse<UsersSegmentation>> => {
    return cachedHttpClient.get('/analytics/users/segmentation', { params: filters });
  },
};

export default usersAnalyticsApi;
